import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react'
import { Check, X, Eye, EyeOff } from 'react-feather'
import { SetPasswordProps } from '../interfaces/formDataInterface'
import { toast } from 'react-toastify'
import axiosReq from '../interceptor'
import useDismissAlert from '../Hooks/useDismissAlert'
import { MessageAlert } from '../components/message-alert'

const SetPassword: React.FC<SetPasswordProps> = ({
  isLoading,
  setIsLoading,
  areFieldsSet,
  onFieldsSet,
  onNext
}) => {
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [error, setError] = useDismissAlert({ message: '' }, 5000)
  const [success, setSuccess] = useState<string>('')
  const email = localStorage.getItem('email')

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Password validation rules.
  const isLengthValid = password.length >= 8
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  const hasSpecialChar = /[!@#$%^&*]/.test(password)
  const passwordsMatch = password === confirmPassword

  // Trigger onFieldsSet whenever the password fields change.
  useEffect(() => {
    const areValidationsPassed =
      isLengthValid &&
      hasUpperCase &&
      hasNumber &&
      hasSpecialChar &&
      passwordsMatch
    onFieldsSet(areValidationsPassed)
  }, [
    password,
    confirmPassword,
    isLengthValid,
    hasUpperCase,
    hasNumber,
    hasSpecialChar,
    passwordsMatch,
    onFieldsSet
  ])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError({ message: '' })
    setSuccess('')
    setIsLoading(true)

    try {
      const response = await axiosReq.post(
        '/app/v2/auth/sign-up-set-password',
        {
          email,
          password
        }
      )

      if (response.status === 200) {
        toast.success('You have successfully set your password!')
        setPassword('')
        setConfirmPassword('')
        onNext()
      } else {
        setError({ message: 'Failed to set password. Please try again.' })
      }
    } catch (error: any) {
      setError({
        message:
          error.response.data.message || 'An error occurred. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Validation message items.
  const validationItems = [
    { text: 'At least 8 characters.', isValid: isLengthValid },
    { text: 'At least one uppercase letter.', isValid: hasUpperCase },
    { text: 'At least one number.', isValid: hasNumber },
    { text: 'At least one symbol.', isValid: hasSpecialChar }
  ]

  return (
    <section>
      <form onSubmit={handleSubmit}>
        <div className="form-outline mb-4 position-relative">
          <label className="form-label text-white" htmlFor="password">
            Password <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              className="form-control"
              placeholder="Enter your password"
              required
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              style={{ borderRadius: '0.375rem' }}
            />
            <div
              className="input-group-append"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </div>
          </div>

          <div className="text-start">
            <p
              className="mb-0 text-white mt-3 mb-3"
              style={{ fontSize: '12px' }}
            >
              Password must contain:
            </p>
            {/* Password Validation Feedback */}
            <div className="row">
              {validationItems.map((item, index) => (
                <div
                  key={index}
                  className="col-6 text-white-50 mb-1"
                  style={{ fontSize: '10px' }}
                >
                  {password.length === 0 ? (
                    <span className="me-1">&bull;</span> // Bullet point by default.
                  ) : item.isValid ? (
                    <Check color="green" size={14} />
                  ) : (
                    <X color="red" size={14} />
                  )}
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="form-outline mb-4 position-relative">
          <label className="form-label text-white" htmlFor="confirmPassword">
            Confirm Password <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              className="form-control mb-3"
              placeholder="Confirm your password"
              required
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              style={{ borderRadius: '0.375rem' }}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <div
                className="input-group-append"
                onClick={() => setConfirmPassword('')}
              >
                <X size={14} color="red" />
              </div>
            )}
          </div>

          {!passwordsMatch && confirmPassword.length > 0 && (
            <MessageAlert message="Passwords don't match!" type="error" />
          )}
        </div>

        {error.message && <MessageAlert message={error.message} type="error" />}

        <button
          className="btn btn-primary rounded-pill w-100 btn-block"
          type="submit"
          disabled={isLoading || !areFieldsSet}
        >
          {isLoading ? 'Setting Password...' : 'Set Password'}
        </button>
      </form>
    </section>
  )
}

export default SetPassword
